import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useAccount, useWishlist } from '@Provider/frontastic';
import CrossbikeHeart from 'components/icons/crossbike-heart';
import HeartIcon from 'components/icons/heart';
import Button from 'components/revelo-ui/content/button';
import Offcanvas from 'components/revelo-ui/products/product-details/offcanvas';
import LineItem from 'components/revelo-ui/products/product-details/offcanvas/line-item';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'helpers/reference';
import { disableScrollOnBody } from 'helpers/utils/disableScrollOnBody';
import InformationCircleIcon from 'components/icons/information-circle';

interface WishListButtonProps {
  wishlistItemCount?: number;
  wishlistLink?: Reference;
}

const WishListButton: React.FC<WishListButtonProps> = ({ wishlistItemCount }) => {
  const router = useRouter();
  const { formatMessage } = useFormat({ name: 'wishlist' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { data: wishlist } = useWishlist();
  const { loggedIn } = useAccount();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setIsOpen(true);
    disableScrollOnBody(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    disableScrollOnBody(false);
  };

  const goToProductPage = (url: string) => {
    handleClose();
    router.push(url);
  };

  const goToLoginpage = () => {
    handleClose();
    router.push('/login');
  };

  const wishlistBody = (
    <div className="pt-6 lg:p-3.5">
      {wishlist?.lineItems?.length === 0 ? (
        <div className="flex items-center gap-8 px-8 py-6">
          <CrossbikeHeart className="shrink-0" />
          <span className="font-bold">
            {formatMessage({
              id: 'wishlist.empty',
              defaultMessage: 'Noch keine Bikes favorisiert',
            })}
          </span>
        </div>
      ) : (
        <>
          {!loggedIn && (
            <>
              <span className="text-sm">
                {formatMessage({
                  id: 'wishlist.login',
                  defaultMessage: 'Um deine Favoriten dauerhaft zu speichern, melde dich bei uns an.',
                })}
              </span>
              <Button onClick={goToLoginpage} style="secondary" className="mb-4 mt-6 w-full">
                {formatAccountMessage({
                  id: 'sign.in',
                  defaultMessage: 'Anmelden',
                })}
              </Button>
            </>
          )}

          <div className="max-h-[60vh] overflow-y-auto md:max-h-[70vh]">
            <ul role="list" className="flex flex-auto flex-col pt-2">
              {wishlist?.lineItems?.map((lineItem, i) => (
                <li key={i} className="relative flex space-x-6 py-2">
                  <LineItem lineItem={lineItem} goToProductPage={goToProductPage} isWishlistItem={true} />
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 flex gap-3 bg-neutral-50 p-4">
            <InformationCircleIcon className="size-6 shrink-0" />
            {formatMessage({
              id: 'wishlist.notReserved',
              defaultMessage: 'Bikes in den Favoriten können nicht reserviert werden. Jedes Bike ist ein Einzelstück.',
            })}
          </div>
        </>
      )}
    </div>
  );

  const wishlistLabel =
    wishlistItemCount === 0
      ? `${formatMessage({ id: 'wishlist' })}, ${formatMessage({ id: 'wishlist.empty' })}`
      : formatMessage({
          id: wishlistItemCount === 1 ? 'wishlist.items.single' : 'wishlist.items.multiple',
          values: { count: wishlistItemCount },
        });

  return (
    <>
      <div className="mx-2 flow-root">
        <button onClick={handleOpen} aria-label={wishlistLabel} className="group relative -m-2 flex items-center p-2">
          <HeartIcon
            className="size-6 shrink-0 fill-neutral-950 hover:fill-neutral-800 dark:text-light-100 dark:group-hover:text-light-100"
            aria-hidden="true"
          />
          {wishlistItemCount > 0 && (
            <span className="absolute right-1 top-1.5 size-2 rounded-full bg-primary-200"></span>
          )}
        </button>
      </div>

      <Offcanvas
        body={wishlistBody}
        title={formatMessage({
          id: 'wishlist.items',
          defaultMessage: 'Deine Favoriten',
        })}
        show={isOpen}
        closeOverlay={handleClose}
        closeLabel={formatMessage({
          id: 'wishlist.close',
          defaultMessage: 'Favoritenliste schließen',
        })}
      />
    </>
  );
};

export default WishListButton;
